import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type {
  EgressClusterMemberEligibility,
  EgressLinkListEntry,
  EgressPolicyRecord,
  UpstreamConfig,
} from '@nadoutong/contracts';
import { OpsApiError } from '../../api/client.ts';
import { useCustomerOwnershipCustomers } from '../../api/customerOwnership.ts';
import {
  useCreateEgressPolicy,
  useDeleteEgressPolicy,
  useEgressPolicies,
  useEffectiveEgressPolicy,
  usePutOrgEgressPolicy,
  useUpdateEgressPolicy,
} from '../../api/useEgressPolicies.ts';
import { useUpstreamsList } from '../../api/useUpstreams.ts';
import { CustomerCompanyLine } from '../../components/identity/CustomerCell.tsx';
import { useToast, coerceErrorMessage } from '../../components/Toast.tsx';
import { useT } from '../../i18n/index.ts';
import {
  Button,
  Chip,
  ConfirmDialog,
  Input,
  Panel,
  Select,
  Switch,
  Table,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../../ui/index.ts';
import type { SelectOption } from '../../ui/index.ts';
import { customerEmailOrNull, customerIdentity } from '../../components/customer-identity.ts';
import { EgressPolicyRuleTable } from './EgressPolicyRuleTable.tsx';
import { memberReasonKey, selectedReasonKey, validationIssueKey } from './labels.ts';
import {
  buildDisplayedRows,
  buildUpdatePayload,
  emptyDraftRule,
  firstValidationIssue,
  inheritedRulesFor,
  pinGlobalFirst,
  toDraftRule,
  type EgressPolicyDraftRule,
} from './policy-rules.ts';

interface PendingCustomer {
  readonly orgId: string;
  readonly email: string | null;
}

export function EgressPolicyTab({
  links,
  ingressRegions,
  onPreviewMembers,
}: {
  readonly links: readonly EgressLinkListEntry[];
  readonly ingressRegions: readonly string[];
  readonly onPreviewMembers: (members: readonly EgressClusterMemberEligibility[]) => void;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const policiesQ = useEgressPolicies();
  const upstreamsQ = useUpstreamsList();
  const createMut = useCreateEgressPolicy();
  const updateMut = useUpdateEgressPolicy();
  const deleteMut = useDeleteEgressPolicy();
  const assignMut = usePutOrgEgressPolicy();
  const policies = pinGlobalFirst(policiesQ.data?.policies ?? []);
  const assignments = policiesQ.data?.assignments ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = policies.find((policy) => policy.id === (selectedId ?? policies[0]?.id)) ?? null;
  const global = policies.find((policy) => policy.is_global_default) ?? null;

  const [drafts, setDrafts] = useState<readonly EgressPolicyDraftRule[]>([]);
  const [emergency, setEmergency] = useState(false);
  const [confirmEmergency, setConfirmEmergency] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [newName, setNewName] = useState('');
  const [assignQuery, setAssignQuery] = useState('');
  const [pendingAssign, setPendingAssign] = useState<PendingCustomer | null>(null);
  const [pendingUnassign, setPendingUnassign] = useState<PendingCustomer | null>(null);
  const [previewOrg, setPreviewOrg] = useState('');
  const [previewOrgEmail, setPreviewOrgEmail] = useState<string | null>(null);
  const [previewIngress, setPreviewIngress] = useState(ingressRegions[0] ?? '');
  const [previewUpstream, setPreviewUpstream] = useState('');
  const [previewArmed, setPreviewArmed] = useState(false);

  useEffect(() => {
    if (selected === null) return;
    setDrafts(selected.rules.map((rule) => toDraftRule(rule)));
    setEmergency(selected.emergency_direct);
    setPendingAssign(null);
    setPendingUnassign(null);
  }, [selected]);

  const inherited = selected === null ? [] : inheritedRulesFor(selected, global);
  const rows = buildDisplayedRows(drafts, inherited);
  const clusters = useMemo(
    () => [...new Set(links.map((link) => link.region))].sort((a, b) => a.localeCompare(b)),
    [links],
  );
  const regions = useMemo(() => {
    const set = new Set<string>([...ingressRegions, ...clusters]);
    for (const upstream of upstreamsQ.data ?? []) {
      if (typeof upstream.region === 'string' && upstream.region.length > 0) set.add(upstream.region);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [clusters, ingressRegions, upstreamsQ.data]);

  const ingressOptions: SelectOption[] = [
    { value: '*', label: t('egress_lines.policies.ingress.any') },
    ...regions.map((region) => ({
      value: region,
      label: t('egress_lines.policies.ingress.gateway', { region }),
    })),
  ];
  const upstreamOptions: SelectOption[] = [
    { value: '*', label: t('egress_lines.policies.upstream.any') },
    ...(upstreamsQ.data ?? []).map((upstream: UpstreamConfig) => ({
      value: `id:${upstream.id}`,
      label: upstream.name,
    })),
  ];
  const viaOptions: SelectOption[] = [
    { value: 'direct', label: t('egress_lines.policies.via.direct') },
    ...clusters.map((region) => ({
      value: region,
      label: t('egress_lines.policies.via.cluster', { region }),
    })),
  ];

  const assignedOrgs = selected === null
    ? []
    : assignments.filter((row) => row.policy_id === selected.id);
  const search = useCustomerOwnershipCustomers({
    limit: 8,
    offset: 0,
    email: assignQuery.trim(),
  });

  const previewQ = useEffectiveEgressPolicy(
    { orgId: previewOrg || undefined, ingress: previewIngress, upstreamId: previewUpstream },
    previewArmed && previewIngress.length > 0 && previewUpstream.length > 0,
  );

  useEffect(() => {
    if (previewQ.data !== undefined) onPreviewMembers(previewQ.data.members);
  }, [onPreviewMembers, previewQ.data]);

  function updateDraft(key: string, next: EgressPolicyDraftRule): void {
    setDrafts((current) => current.map((row) => (row.key === key ? next : row)));
  }

  async function handleSave(): Promise<void> {
    if (selected === null) return;
    const issue = firstValidationIssue(drafts);
    if (issue !== null) {
      const key = validationIssueKey(issue);
      toast.error(key === null ? issue : t(key));
      return;
    }
    try {
      await updateMut.mutateAsync({
        policyId: selected.id,
        body: buildUpdatePayload(selected, drafts, emergency),
      });
      toast.success(t('egress_lines.policies.saved'));
    } catch (error) {
      if (error instanceof OpsApiError && error.code === 'EGRESS_POLICY_VERSION_CONFLICT') {
        toast.error(t('egress_lines.policies.version_conflict'));
        void policiesQ.refetch();
        return;
      }
      toast.error(error instanceof OpsApiError ? error.message : coerceErrorMessage(error));
    }
  }

  async function handleDelete(): Promise<void> {
    if (selected === null) return;
    try {
      await deleteMut.mutateAsync(selected.id);
      setConfirmDelete(false);
      setSelectedId(global?.id ?? null);
      toast.success(t('egress_lines.policies.deleted'));
    } catch (error) {
      toast.error(error instanceof OpsApiError ? error.message : coerceErrorMessage(error));
    }
  }

  async function handleCreate(): Promise<void> {
    const name = newName.trim();
    if (name.length === 0) {
      toast.error(t('egress_lines.policies.create_name_required'));
      return;
    }
    try {
      const created = await createMut.mutateAsync({
        name,
        rules: [{ ingress_region: '*', via_kind: 'direct', fallback: 'direct' }],
      });
      setNewName('');
      setSelectedId(created.policy.id);
      toast.success(t('egress_lines.policies.created'));
    } catch (error) {
      toast.error(error instanceof OpsApiError ? error.message : coerceErrorMessage(error));
    }
  }

  async function handleAssign(customer: PendingCustomer): Promise<void> {
    if (selected === null) return;
    try {
      await assignMut.mutateAsync({ orgId: customer.orgId, body: { policy_id: selected.id } });
      setAssignQuery('');
      setPendingAssign(null);
      toast.success(t('egress_lines.policies.assigned'));
    } catch (error) {
      toast.error(error instanceof OpsApiError ? error.message : coerceErrorMessage(error));
    }
  }

  async function handleUnassign(customer: PendingCustomer): Promise<void> {
    try {
      await assignMut.mutateAsync({ orgId: customer.orgId, body: { policy_id: null } });
      setPendingUnassign(null);
      toast.success(t('egress_lines.policies.unassigned'));
    } catch (error) {
      toast.error(error instanceof OpsApiError ? error.message : coerceErrorMessage(error));
    }
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[260px_1fr]" data-testid="egress-policy-tab">
      <div className="flex flex-col gap-4">
        <Panel caption={t('egress_lines.policies.list_title')} padded testId="egress-policy-list">
          <div className="mb-3 flex gap-2">
            <Input
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              placeholder={t('egress_lines.policies.new_name')}
              data-testid="egress-policy-new-name"
            />
            <Button size="sm" onClick={() => { void handleCreate(); }} loading={createMut.isPending} data-testid="egress-policy-create">
              {t('egress_lines.policies.new')}
            </Button>
          </div>
          {policies.length === 0 ? (
            <p className="m-0 text-dense text-paper-low">{t('egress_lines.policies.empty')}</p>
          ) : (
            <ul className="m-0 flex list-none flex-col gap-1 p-0">
              {policies.map((policy) => {
                const active = selected?.id === policy.id;
                return (
                  <li key={policy.id}>
                    <button
                      type="button"
                      className={`w-full rounded-md px-3 py-2 text-left ${active ? 'bg-signal-dim' : 'bg-well hover:bg-active'}`}
                      onClick={() => setSelectedId(policy.id)}
                      data-testid={`egress-policy-item-${policy.id}`}
                    >
                      <span className="flex items-center gap-2">
                        <span className="text-dense font-medium text-paper">{policy.name}</span>
                        {policy.is_global_default ? (
                          <Chip tone="signal" lamp={false}>{t('egress_lines.policies.floor_badge')}</Chip>
                        ) : null}
                      </span>
                      <span className="mt-1 block text-micro text-paper-low">
                        {t('egress_lines.policies.rule_count', { count: String(policy.rules.length) })}
                        {' · '}
                        {policy.is_global_default
                          ? t('egress_lines.policies.unassigned_customers')
                          : t('egress_lines.policies.assigned_count', { count: String(policy.assigned_org_count) })}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>

        <Panel caption={t('egress_lines.policies.assigned_title')} padded testId="egress-policy-assigned">
          {assignedOrgs.length === 0 ? (
            <p className="m-0 text-dense text-paper-low">{t('egress_lines.policies.no_assignments')}</p>
          ) : (
            <ul className="m-0 mb-3 flex list-none flex-col gap-1 p-0">
              {assignedOrgs.map((row) => (
                <li
                  key={row.org_id}
                  className="flex items-center justify-between gap-2 rounded bg-well px-3 py-2"
                  data-testid={`egress-policy-assigned-${row.org_id}`}
                >
                  <Link className="min-w-0 text-dense text-paper" to={`/crm/customers/${encodeURIComponent(row.org_id)}`}>
                    <AssignedCustomerLabel
                      orgId={row.org_id}
                      email={row.email}
                      testIdPrefix="egress-policy-assigned-company"
                    />
                  </Link>
                  {selected?.is_global_default !== true ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setPendingUnassign({
                        orgId: row.org_id,
                        email: customerEmailOrNull(row.email),
                      })}
                      data-testid={`egress-policy-unassign-${row.org_id}`}
                    >
                      {t('egress_lines.policies.unassign')}
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {selected !== null && !selected.is_global_default ? (
            <div className="flex flex-col gap-2">
              <Input
                value={assignQuery}
                onChange={(event) => setAssignQuery(event.target.value)}
                placeholder={t('egress_lines.policies.assign_placeholder')}
                data-testid="egress-policy-assign-search"
              />
              {(search.data?.customers ?? []).map((customer) => (
                <button
                  key={customer.org_id}
                  type="button"
                  className="rounded bg-well px-3 py-2 text-left text-dense text-paper hover:bg-active"
                  onClick={() => setPendingAssign({
                    orgId: customer.org_id,
                    email: customerEmailOrNull(customer.email),
                  })}
                  data-testid={`egress-policy-assign-${customer.org_id}`}
                >
                  <AssignedCustomerLabel
                    orgId={customer.org_id}
                    email={customer.email}
                    testIdPrefix="egress-policy-assign-company"
                  />
                </button>
              ))}
            </div>
          ) : null}
        </Panel>
      </div>

      {selected === null ? (
        <Panel caption={t('egress_lines.policies.list_title')} padded>
          <p className="m-0 text-dense text-paper-low">{t('egress_lines.policies.empty')}</p>
        </Panel>
      ) : (
        <div className="flex flex-col gap-4">
          <Panel caption={selected.name} padded testId="egress-policy-editor">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <h3 className="m-0 text-body font-medium text-paper">{selected.name}</h3>
                <Chip tone="neutral" lamp={false}>{t('egress_lines.policies.version', { version: String(selected.version) })}</Chip>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Switch
                  checked={emergency}
                  onCheckedChange={(checked) => {
                    if (checked) setConfirmEmergency(true);
                    else setEmergency(false);
                  }}
                  label={t('egress_lines.policies.emergency')}
                  testId="egress-policy-emergency"
                />
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setPreviewArmed(true)}
                  data-testid="egress-policy-preview-btn"
                >
                  {t('egress_lines.policies.preview')}
                </Button>
                <Button
                  size="sm"
                  onClick={() => { void handleSave(); }}
                  loading={updateMut.isPending}
                  data-testid="egress-policy-save"
                >
                  {t('egress_lines.policies.save')}
                </Button>
                {!selected.is_global_default && selected.assigned_org_count === 0 ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setConfirmDelete(true)}
                    data-testid="egress-policy-delete"
                  >
                    {t('egress_lines.policies.delete')}
                  </Button>
                ) : null}
              </div>
            </div>
            <p className="m-0 mb-3 text-dense text-paper-low">{t('egress_lines.policies.inherit_note')}</p>
            <EgressPolicyRuleTable
              rows={rows}
              ingressOptions={ingressOptions}
              upstreamOptions={upstreamOptions}
              viaOptions={viaOptions}
              onChange={updateDraft}
              onRemove={(key) => setDrafts((current) => current.filter((row) => row.key !== key))}
            />
            <div className="mt-3">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setDrafts((current) => [...current, emptyDraftRule(`draft-${Date.now()}`)])}
                data-testid="egress-policy-add-rule"
              >
                {t('egress_lines.policies.add_rule')}
              </Button>
            </div>
            <ConfirmDialog
              open={confirmEmergency}
              onOpenChange={(open) => { if (!open) setConfirmEmergency(false); }}
              title={t('egress_lines.policies.emergency_confirm_title')}
              body={t('egress_lines.policies.emergency_confirm_body')}
              confirmLabel={t('egress_lines.policies.emergency')}
              cancelLabel={t('common.cancel')}
              closeLabel={t('common.close')}
              tone="danger"
              onConfirm={() => {
                setEmergency(true);
                setConfirmEmergency(false);
              }}
              testId="egress-policy-emergency-confirm"
            />
            <ConfirmDialog
              open={confirmDelete}
              onOpenChange={(open) => { if (!open) setConfirmDelete(false); }}
              title={t('egress_lines.policies.delete_confirm_title')}
              body={t('egress_lines.policies.delete_confirm_body')}
              confirmLabel={t('egress_lines.policies.delete')}
              cancelLabel={t('common.cancel')}
              closeLabel={t('common.close')}
              tone="danger"
              loading={deleteMut.isPending}
              onConfirm={() => { void handleDelete(); }}
              testId="egress-policy-delete-confirm"
            />
          </Panel>

          <PreviewCard
            previewOrg={previewOrg}
            previewOrgEmail={previewOrgEmail}
            previewIngress={previewIngress}
            previewUpstream={previewUpstream}
            ingressOptions={ingressOptions.filter((option) => option.value !== '*')}
            upstreamOptions={(upstreamsQ.data ?? []).map((upstream) => ({
              value: upstream.id,
              label: upstream.name,
            }))}
            onOrgChange={(orgId, email) => {
              setPreviewOrg(orgId);
              setPreviewOrgEmail(email);
            }}
            onIngressChange={setPreviewIngress}
            onUpstreamChange={setPreviewUpstream}
            onResolve={() => setPreviewArmed(true)}
            result={previewQ.data}
          />
          <ConfirmDialog
            open={pendingAssign !== null}
            onOpenChange={(open) => { if (!open) setPendingAssign(null); }}
            title={t('egress_lines.policies.assign_confirm_title')}
            body={pendingAssign === null || selected === null
              ? undefined
              : t('egress_lines.policies.assign_confirm_body', {
                customer: customerIdentity(pendingAssign.orgId, pendingAssign.email),
                policy: selected.name,
              })}
            confirmLabel={t('egress_lines.policies.assign')}
            cancelLabel={t('common.cancel')}
            closeLabel={t('common.close')}
            loading={assignMut.isPending}
            onConfirm={() => {
              if (pendingAssign !== null) void handleAssign(pendingAssign);
            }}
            testId="egress-policy-assign-confirm"
          />
          <ConfirmDialog
            open={pendingUnassign !== null}
            onOpenChange={(open) => { if (!open) setPendingUnassign(null); }}
            title={t('egress_lines.policies.unassign_confirm_title')}
            body={pendingUnassign === null || selected === null
              ? undefined
              : t('egress_lines.policies.unassign_confirm_body', {
                customer: customerIdentity(pendingUnassign.orgId, pendingUnassign.email),
                policy: selected.name,
              })}
            confirmLabel={t('egress_lines.policies.unassign')}
            cancelLabel={t('common.cancel')}
            closeLabel={t('common.close')}
            loading={assignMut.isPending}
            onConfirm={() => {
              if (pendingUnassign !== null) void handleUnassign(pendingUnassign);
            }}
            testId="egress-policy-unassign-confirm"
          />
        </div>
      )}
    </div>
  );
}

function AssignedCustomerLabel({
  orgId,
  email,
  testIdPrefix,
}: {
  readonly orgId: string;
  readonly email: string | null | undefined;
  readonly testIdPrefix: string;
}): JSX.Element {
  const t = useT();
  const trimmed = customerEmailOrNull(email);
  const today = trimmed !== null ? (
    <span className="font-mono">{customerIdentity(orgId, trimmed)}</span>
  ) : (
    <span className="font-mono">
      {orgId}
      <span className="ml-2 text-paper-low">{t('egress_lines.policies.no_email')}</span>
    </span>
  );
  return (
    <span className="flex min-w-0 flex-col gap-0.5">
      <CustomerCompanyLine orgId={orgId} testId={`${testIdPrefix}-${orgId}`} />
      {today}
    </span>
  );
}

function PreviewCard({
  previewOrg,
  previewOrgEmail,
  previewIngress,
  previewUpstream,
  ingressOptions,
  upstreamOptions,
  onOrgChange,
  onIngressChange,
  onUpstreamChange,
  onResolve,
  result,
}: {
  readonly previewOrg: string;
  readonly previewOrgEmail: string | null;
  readonly previewIngress: string;
  readonly previewUpstream: string;
  readonly ingressOptions: readonly SelectOption[];
  readonly upstreamOptions: readonly SelectOption[];
  readonly onOrgChange: (orgId: string, email: string | null) => void;
  readonly onIngressChange: (value: string) => void;
  readonly onUpstreamChange: (value: string) => void;
  readonly onResolve: () => void;
  readonly result: ReturnType<typeof useEffectiveEgressPolicy>['data'];
}): JSX.Element {
  const t = useT();
  const [previewQuery, setPreviewQuery] = useState(previewOrg);
  const search = useCustomerOwnershipCustomers({
    limit: 8,
    offset: 0,
    email: previewQuery.trim(),
  });
  return (
    <Panel caption={t('egress_lines.policies.preview_title')} padded testId="egress-policy-preview">
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <div className="min-w-[12rem] flex-1">
          <label className="mb-1 block text-micro text-paper-low">{t('egress_lines.policies.preview_org')}</label>
          <Input
            value={previewQuery}
            onChange={(event) => {
              const next = event.target.value;
              setPreviewQuery(next);
              const trimmed = next.trim();
              if (trimmed.startsWith('org_') && !trimmed.includes(' ')) {
                onOrgChange(trimmed, null);
              } else {
                onOrgChange('', null);
              }
            }}
            placeholder={t('egress_lines.policies.assign_placeholder')}
            data-testid="egress-policy-preview-org"
          />
          {previewOrg.length > 0 ? (
            <p className="m-0 mt-1 font-mono text-micro text-paper" data-testid="egress-policy-preview-org-selected">
              <AssignedCustomerLabel
                orgId={previewOrg}
                email={previewOrgEmail}
                testIdPrefix="egress-policy-preview-company"
              />
            </p>
          ) : null}
          {(search.data?.customers ?? []).slice(0, 4).map((customer) => (
            <button
              key={customer.org_id}
              type="button"
              className="mt-1 block w-full rounded bg-well px-2 py-1 text-left text-micro text-paper"
              onClick={() => {
                setPreviewQuery(customerIdentity(customer.org_id, customer.email));
                onOrgChange(customer.org_id, customerEmailOrNull(customer.email));
              }}
              data-testid={`egress-policy-preview-org-${customer.org_id}`}
            >
              <AssignedCustomerLabel
                orgId={customer.org_id}
                email={customer.email}
                testIdPrefix="egress-policy-preview-pick-company"
              />
            </button>
          ))}
        </div>
        <div className="min-w-[8rem]">
          <label className="mb-1 block text-micro text-paper-low">{t('egress_lines.policies.preview_ingress')}</label>
          {ingressOptions.length === 0 ? (
            <p className="m-0 text-dense text-paper-low">{t('egress_lines.policies.preview_ingress_empty')}</p>
          ) : (
            <Select
              value={previewIngress.length > 0 ? previewIngress : undefined}
              options={ingressOptions}
              placeholder={t('egress_lines.policies.preview_ingress')}
              onValueChange={onIngressChange}
              testId="egress-policy-preview-ingress"
            />
          )}
        </div>
        <div className="min-w-[10rem] flex-1">
          <label className="mb-1 block text-micro text-paper-low">{t('egress_lines.policies.preview_upstream')}</label>
          <Select
            value={previewUpstream.length > 0 ? previewUpstream : undefined}
            options={upstreamOptions}
            placeholder={t('egress_lines.policies.preview_upstream')}
            onValueChange={onUpstreamChange}
            testId="egress-policy-preview-upstream"
          />
        </div>
        <Button size="sm" onClick={onResolve} data-testid="egress-policy-preview-resolve">
          {t('egress_lines.policies.preview_resolve')}
        </Button>
      </div>
      {result === undefined ? null : (
        <div className="grid gap-4 md:grid-cols-2" data-testid="egress-policy-preview-result">
          <dl className="m-0 grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1 text-dense">
            <dt className="text-paper-low">{t('egress_lines.policies.preview.via')}</dt>
            <dd className="m-0 font-mono">
              {result.via.kind === 'cluster' ? result.via.cluster : t('egress_lines.policies.via.direct')}
            </dd>
            <dt className="text-paper-low">{t('egress_lines.policies.preview.matched')}</dt>
            <dd className="m-0 font-mono">
              {result.policy_id}
              {result.rule_index === null
                ? ''
                : ` · ${t('egress_lines.policies.preview.rule_index', { index: String(result.rule_index + 1) })}`}
            </dd>
            <dt className="text-paper-low">{t('egress_lines.policies.preview.inherited')}</dt>
            <dd className="m-0">
              {result.inherited_from_global
                ? t('egress_lines.policies.preview.inherited_yes')
                : t('egress_lines.policies.preview.inherited_no')}
            </dd>
            <dt className="text-paper-low">{t('egress_lines.policies.preview.fallback')}</dt>
            <dd className="m-0 font-mono">{result.fallback}</dd>
            <dt className="text-paper-low">{t('egress_lines.policies.preview.policy_version')}</dt>
            <dd className="m-0 font-mono" data-testid="egress-policy-preview-version">
              {result.policy_version === null ? t('egress_lines.policies.fallback.na') : `v${String(result.policy_version)}`}
            </dd>
            <dt className="text-paper-low">{t('egress_lines.policies.preview.cached_at')}</dt>
            <dd className="m-0 font-mono" data-testid="egress-policy-preview-cached-at">
              {result.cached_at ?? t('egress_lines.policies.fallback.na')}
            </dd>
            <dt className="text-paper-low">{t('egress_lines.policies.preview.selected_path')}</dt>
            <dd className="m-0 font-mono" data-testid="egress-policy-preview-path">{result.selected_path_id}</dd>
            <dt className="text-paper-low">{t('egress_lines.policies.preview.selected_reason')}</dt>
            <dd className="m-0" data-testid="egress-policy-preview-reason">{t(selectedReasonKey(result.selected_reason))}</dd>
            <dt className="text-paper-low">{t('egress_lines.policies.preview.cluster_down')}</dt>
            <dd className="m-0" data-testid="egress-policy-preview-cluster-down">
              {result.cluster_down
                ? t('egress_lines.policies.preview.cluster_down_yes')
                : t('egress_lines.policies.preview.cluster_down_no')}
            </dd>
          </dl>
          <TableLite members={result.members} />
        </div>
      )}
    </Panel>
  );
}

function TableLite({ members }: { readonly members: readonly EgressClusterMemberEligibility[] }): JSX.Element {
  const t = useT();
  return (
    <Table>
      <THead>
        <Tr>
          <Th>{t('egress_lines.policies.preview.member_id')}</Th>
          <Th>{t('egress_lines.policies.preview.member_eligible')}</Th>
          <Th>{t('egress_lines.policies.preview.member_reason')}</Th>
        </Tr>
      </THead>
      <TBody>
        {members.map((member) => (
          <Tr key={member.egress_id}>
            <Td className="font-mono">{member.egress_id}</Td>
            <Td>{member.eligible ? t('egress_lines.policies.preview.yes') : t('egress_lines.policies.preview.no')}</Td>
            <Td className="text-paper-low">{t(memberReasonKey(member.reason))}</Td>
          </Tr>
        ))}
      </TBody>
    </Table>
  );
}
